// The image converter on /tools/convert.
//
// Files are decoded, redrawn onto a canvas and encoded again, all by the
// browser. Nothing is uploaded, which is also why the result cannot be better
// than what this browser can encode: the format list is filtered by what it
// actually produces rather than by what it accepts as an argument.
//
// Every setting re-runs the whole list, so what is on screen always matches the
// controls above it and there is no stale "convert" button to forget to press.

import {
    OUTPUT_FORMATS,
    canEncode,
    decode,
    sizeOf,
    fitWithin,
    draw,
    encode,
    renameExtension,
    saveBlob,
    formatBytes,
} from './tools/images.js';
import { wirePicker, blockStrayDrops, initColourPicker } from './tools/picker.js';
import { zip } from './tools/zip.js';
import { wireAccountSave } from './tools/account.js';

// Long enough that dragging the quality slider does not queue a conversion per
// pixel of travel, short enough that letting go feels immediate.
const SETTLE_MS = 250;

const el = id => document.getElementById(id);

const dom = {
    zone: el('convertZone'),
    input: el('convertInput'),
    panel: el('convertPanel'),
    format: el('convertFormat'),
    formatNote: el('convertFormatNote'),
    qualityRow: el('convertQualityRow'),
    quality: el('convertQuality'),
    qualityValue: el('convertQualityValue'),
    resize: el('convertResize'),
    resizeRow: el('convertResizeRow'),
    maxWidth: el('convertMaxWidth'),
    maxHeight: el('convertMaxHeight'),
    backgroundRow: el('convertBackgroundRow'),
    background: el('convertBackground'),
    list: el('convertList'),
    summary: el('convertSummary'),
    downloadAll: el('convertDownloadAll'),
    clear: el('convertClear'),
    saveAccount: el('toolSaveAccount'),
    savePanel: el('toolSavePanel'),
    saveList: el('toolSaveList'),
    saveError: el('toolSaveError'),
};

// One entry per picked file. `output` is replaced on every run; the object URL
// alongside it is revoked first so a long session does not leak one per run.
const items = [];
let runToken = 0;
let settleTimer = null;

function settings() {
    const format = OUTPUT_FORMATS.find(f => f.mime === dom.format.value) || OUTPUT_FORMATS[0];
    return {
        format,
        quality: format.lossy ? Number(dom.quality.value) / 100 : undefined,
        maxWidth: dom.resize.checked ? Number(dom.maxWidth.value) || 0 : 0,
        maxHeight: dom.resize.checked ? Number(dom.maxHeight.value) || 0 : 0,
        // Only meaningful without an alpha channel. Drawing it under an image
        // that keeps its transparency would fill in the very thing the visitor
        // wanted to keep.
        background: format.mime === 'image/jpeg' ? dom.background.value : null,
    };
}

function syncControls() {
    const format = OUTPUT_FORMATS.find(f => f.mime === dom.format.value) || OUTPUT_FORMATS[0];
    dom.formatNote.textContent = format.note;
    dom.qualityRow.hidden = !format.lossy;
    dom.backgroundRow.hidden = format.mime !== 'image/jpeg';
    dom.resizeRow.hidden = !dom.resize.checked;
    dom.qualityValue.textContent = `${dom.quality.value}%`;
}

function row(item) {
    const node = document.createElement('li');
    node.className = 'flex items-center gap-4 p-4';
    node.innerHTML = `
        <div class="h-14 w-14 shrink-0 rounded-xl overflow-hidden ring-1 ring-slate-200 dark:ring-white/10 bg-slate-100 dark:bg-gray-900/50">
            <img data-thumb alt="" class="h-full w-full object-cover" />
        </div>
        <div class="min-w-0 flex-1">
            <p data-name class="truncate text-sm font-semibold text-slate-900 dark:text-white"></p>
            <p data-detail class="mt-0.5 text-xs text-slate-500 dark:text-slate-400">Waiting</p>
        </div>
        <div class="flex items-center gap-2 shrink-0">
            <span data-saving class="hidden rounded-full px-2.5 py-1 text-xs font-semibold"></span>
            <button type="button" data-save disabled
                class="inline-flex items-center gap-2 rounded-xl px-3.5 py-2 text-sm font-medium bg-slate-100 dark:bg-gray-700 border border-slate-200 dark:border-gray-600 text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-gray-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                <i class="fa-regular fa-arrow-down-to-line"></i>
                <span class="hidden sm:inline">Save</span>
            </button>
            <button type="button" data-remove aria-label="Remove"
                class="inline-flex items-center justify-center h-9 w-9 rounded-xl text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:text-red-400 dark:hover:bg-red-900/20 transition-colors">
                <i class="fa-regular fa-xmark"></i>
            </button>
        </div>`;

    node.querySelector('[data-name]').textContent = item.file.name;
    node.querySelector('[data-save]').addEventListener('click', () => {
        if (item.output) saveBlob(item.output.blob, item.output.name);
    });
    node.querySelector('[data-remove]').addEventListener('click', () => remove(item));

    item.node = node;
    return node;
}

function paint(item) {
    if (!item.node) return;

    const thumb = item.node.querySelector('[data-thumb]');
    const detail = item.node.querySelector('[data-detail]');
    const saving = item.node.querySelector('[data-saving]');
    const save = item.node.querySelector('[data-save]');

    if (item.error) {
        detail.textContent = item.error;
        detail.className = 'mt-0.5 text-xs text-red-600 dark:text-red-400';
        saving.classList.add('hidden');
        save.disabled = true;
        return;
    }

    detail.className = 'mt-0.5 text-xs text-slate-500 dark:text-slate-400';

    if (!item.output) {
        detail.textContent = 'Converting';
        saving.classList.add('hidden');
        save.disabled = true;
        return;
    }

    const { blob, name, width, height } = item.output;
    thumb.src = item.output.preview;
    detail.textContent = `${width} x ${height} - ${formatBytes(item.file.size)} to ${formatBytes(blob.size)} - ${name}`;
    save.disabled = false;

    const change = Math.round((1 - blob.size / item.file.size) * 100);
    saving.classList.remove('hidden');
    saving.textContent = change > 0 ? `-${change}%` : `+${Math.abs(change)}%`;
    saving.className = change > 0
        ? 'rounded-full px-2.5 py-1 text-xs font-semibold bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
        : 'rounded-full px-2.5 py-1 text-xs font-semibold bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300';
}

function paintSummary() {
    const done = items.filter(item => item.output);
    dom.downloadAll.disabled = done.length === 0;

    if (!items.length) {
        dom.summary.textContent = '';
        return;
    }

    // A file that could not be decoded is finished as far as the run is
    // concerned, otherwise one bad file leaves the count counting forever.
    const settled = items.filter(item => item.output || item.error).length;
    if (settled < items.length) {
        dom.summary.textContent = `Converting ${settled + 1} of ${items.length}`;
        return;
    }

    if (!done.length) {
        dom.summary.textContent = 'Nothing could be converted';
        return;
    }

    const before = done.reduce((sum, item) => sum + item.file.size, 0);
    const after = done.reduce((sum, item) => sum + item.output.blob.size, 0);
    const change = before ? Math.round((1 - after / before) * 100) : 0;
    const direction = change > 0 ? `${change}% smaller` : `${Math.abs(change)}% larger`;
    dom.summary.textContent = `${done.length} ${done.length === 1 ? 'image' : 'images'}, ${formatBytes(before)} to ${formatBytes(after)}, ${direction}`;
}

function releaseOutput(item) {
    if (item.output) URL.revokeObjectURL(item.output.preview);
    item.output = null;
}

async function convertOne(item, config) {
    const source = sizeOf(item.image);
    const target = config.maxWidth || config.maxHeight
        ? fitWithin(source.width, source.height, config.maxWidth, config.maxHeight)
        : source;

    const canvas = draw(item.image, target.width, target.height, config.background);
    const blob = await encode(canvas, config.format.mime, config.quality);

    return {
        blob,
        name: renameExtension(item.file.name, config.format.extension),
        width: target.width,
        height: target.height,
        preview: URL.createObjectURL(blob),
    };
}

/**
 * Convert everything with the current settings.
 *
 * One at a time. A dozen large canvases encoding in parallel is where a tab
 * runs out of memory, and the encoders are busy on the same thread anyway.
 * The token makes a run started by a newer setting win over an older one still
 * working through the list.
 */
async function run() {
    const token = (runToken += 1);
    const config = settings();

    for (const item of items) {
        releaseOutput(item);
        // An error from encoding is about the settings that produced it, so it
        // is cleared. One from decoding is about the file and has to stay: that
        // item never gets another attempt.
        if (item.image) item.error = null;
        paint(item);
    }
    paintSummary();

    // Iterated over a snapshot: removing a row while the list is still working
    // through it would otherwise make the loop skip the entry after it.
    for (const item of [...items]) {
        if (token !== runToken) return;
        // Removed while this run was working, or a file that would not decode
        // and keeps the reason it gave.
        if (!items.includes(item) || !item.image) continue;
        try {
            const output = await convertOne(item, config);
            if (token !== runToken) {
                URL.revokeObjectURL(output.preview);
                return;
            }
            item.output = output;
            item.error = null;
        } catch (err) {
            item.error = err.message || 'Could not convert this image';
        }
        paint(item);
        paintSummary();
    }
}

function schedule() {
    clearTimeout(settleTimer);
    settleTimer = setTimeout(run, SETTLE_MS);
}

function remove(item) {
    const index = items.indexOf(item);
    if (index === -1) return;
    releaseOutput(item);
    item.node?.remove();
    if (item.image?.close) item.image.close();
    items.splice(index, 1);
    paintSummary();
    togglePanel();
}

// The drop zone stays put; only the settings and the list come and go. Dropping
// a second batch on top of the first is the normal way to use this.
function togglePanel() {
    dom.panel.hidden = items.length === 0;
}

async function add(files) {
    for (const file of files) {
        const item = { file, image: null, output: null, error: null, node: null };
        try {
            item.image = await decode(file);
        } catch (err) {
            item.error = err.message || 'Could not read this image';
        }
        items.push(item);
        dom.list.appendChild(row(item));
        paint(item);
    }

    togglePanel();
    // Files that failed to decode have nothing to convert, but they stay in the
    // list with their reason rather than disappearing without explanation.
    run();
}

async function downloadAll() {
    const done = items.filter(item => item.output);
    if (!done.length) return;

    if (done.length === 1) {
        saveBlob(done[0].output.blob, done[0].output.name);
        return;
    }

    dom.downloadAll.disabled = true;
    try {
        // Two files converted from photo.png and photo.jpg both want photo.webp,
        // and a ZIP with two identical names only unpacks as one.
        const used = new Map();
        const entries = done.map(item => {
            const name = item.output.name;
            const seen = used.get(name) || 0;
            used.set(name, seen + 1);
            return {
                name: seen ? name.replace(/(\.[^.]+)$/, ` (${seen})$1`) : name,
                blob: item.output.blob,
            };
        });
        saveBlob(await zip(entries), 'converted-images.zip');
    } finally {
        dom.downloadAll.disabled = false;
    }
}

async function fillFormats() {
    const supported = [];
    for (const format of OUTPUT_FORMATS) {
        if (await canEncode(format.mime)) supported.push(format);
    }

    dom.format.innerHTML = '';
    for (const format of supported) {
        const option = document.createElement('option');
        option.value = format.mime;
        option.textContent = format.label;
        dom.format.appendChild(option);
    }

    // The format a landing page such as /tools/png-to-webp is named after, and
    // otherwise JPEG, which is the conversion people come to this page for.
    const wanted = dom.format.dataset.preferred || 'image/jpeg';
    const preferred = supported.find(format => format.mime === wanted)
        || supported.find(format => format.mime === 'image/jpeg')
        || supported[0];
    dom.format.value = preferred.mime;
    syncControls();
}

function init() {
    if (!dom.zone) return;

    blockStrayDrops();
    initColourPicker();
    wirePicker({ zone: dom.zone, input: dom.input, multiple: true, onFiles: add });

    fillFormats();

    dom.format.addEventListener('change', () => { syncControls(); run(); });
    dom.quality.addEventListener('input', () => { syncControls(); schedule(); });
    dom.resize.addEventListener('change', () => { syncControls(); run(); });
    dom.maxWidth.addEventListener('input', schedule);
    dom.maxHeight.addEventListener('input', schedule);
    dom.background.addEventListener('input', schedule);

    dom.downloadAll.addEventListener('click', downloadAll);

    wireAccountSave({
        button: dom.saveAccount,
        panel: dom.savePanel,
        list: dom.saveList,
        error: dom.saveError,
        collect: () => items
            .filter(item => item.output)
            .map(item => ({ blob: item.output.blob, name: item.output.name })),
    });
    dom.clear.addEventListener('click', () => {
        while (items.length) remove(items[0]);
    });
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
} else {
    init();
}
